import * as React from 'react';

import * as Bluebird from 'bluebird';
import { v4 as UUID } from 'uuid';

import { makeStyles } from '@material-ui/core/styles';

import { Switch, Route, useParams, useRouteMatch, useHistory, useLocation } from 'react-router-dom';
import {
    Grid,
    Paper,
    Typography,
    AppBar, Tabs, Tab, Toolbar,
    Button, IconButton,
    FormControl, InputLabel, Select, MenuItem, TextField,
    Divider,
    Dialog, DialogTitle, DialogContent, DialogActions
} from '@material-ui/core';
import * as Icons from '@material-ui/icons';

import { AgGridReact, AgGridColumn } from 'ag-grid-react';

import { useSnackbar } from 'notistack';

import { LouverSelector, LouverSelectorProps } from '../louverSelector';
import { LayoutEntity, LayoutEntityProps } from '../layoutEntity';
import { LayoutGrid, LayoutGridProps } from '../layoutGrid';

import { useServiceClient, usePreferences } from '../contexts';
import { CatalogEntry } from '../../lib/serviceClient';

import * as LouverSelectionService from '../../lib/services/louverSelection.selection';
import * as ConfigurationModelEntity from '../../lib/services/configuration.modelEntity';
import * as ConfigurationModelLayout from '../../lib/services/configuration.modelLayout';
import * as ConfigurationModelConstraint from '../../lib/services/configuration.modelConstraint';
import * as ConfigurationCore from '../../lib/services/configuration.core';
import * as ConfigurationLayout from '../../lib/services/configuration.layout';
import { Entity } from '../../lib/services/configuration.projectionLayout';
import * as ConfigurationLayoutGrid from '../../lib/services/configuration.projectionLayoutGrid';

interface DataAccessTab {
    name: string;
    label: string;
    element: React.ComponentType<ProductEntityTabProps>;
}
const tabs: ReadonlyArray<DataAccessTab> = [
    {
        name: 'sizing',
        label: 'Sizing',
        element: SizingTab
    },
    {
        name: 'accessories',
        label: 'Accessories',
        element: AccessoriesTab
    }
]

const styles = makeStyles((theme) => ({
    root: {
        // background: '#c6cacf'
    },
    grow: {
        flexGrow: 1
    },
    sizesToolbar: {
        alignItems: 'flex-start'
    },
    sizesToolbarHeader: {
        flexGrow: 1
    },
    formControl: {
        minWidth: 150,
        paddingBottom: theme.spacing(1)
    },
    paperContainer: {
        padding: theme.spacing(1)
    },
    entityContainer: {
        padding: theme.spacing(1),
        background: '#F2F3F4'
    },
    textarea: {
        // resize: 'vertical',
        fontSize: 8,
        overflow: 'auto',
        maxWidth: '100%',
        maxHeight: 400
        // whiteSpace: 'nowrap'
    },
}));

export function LouverSelection() {
    const classes = styles();

    const { tab } = useParams<{ tab?: string }>()
    const history = useHistory();

    const { enqueueSnackbar } = useSnackbar();

    const serviceClient = useServiceClient();
    const [ preferences, setPreferences ] = usePreferences();

    const [ selectorCoreEntity, setSelectorCoreEntity ] = React.useState<ConfigurationCore.Entity>();
    React.useEffect(() => {(async () => {
        // const entityModel = await serviceClient.getEntity('LV/Louver_ConfigurationSelector_Schema');
        // const coreEntity = await serviceClient.createCoreEntity(entityModel);
        const coreEntity = await serviceClient.createLouverSelectorCoreEntity();
        console.log('CORE ENTITY', JSON.parse(JSON.stringify(coreEntity)))

        setSelectorCoreEntity(coreEntity);
    })().catch(err => {
        enqueueSnackbar('[Selector Entity Creation] ' + err.toString(), { variant: 'error' });
        console.error(err)
    })}, [ serviceClient ]);

    const [ selectorLayoutEntity, setSelectorLayoutEntity ] = React.useState<ConfigurationLayout.Entity>();
    React.useEffect(() => {(async () => {
        if (!selectorCoreEntity)
            return;

        const layoutEntity = await serviceClient.createLayoutEntity(selectorCoreEntity);

        setSelectorLayoutEntity(layoutEntity);
        enqueueSnackbar('Selector layout loaded', { variant: 'info' });
    })().catch(err => {
        enqueueSnackbar('[Selector Layout Creation] ' + err.toString(), { variant: 'error' });
        console.error(err)
    })}, [ serviceClient, selectorCoreEntity ]);

    const [ louverSizes, setLouverSizes ] = React.useState<{ coreEntity: ConfigurationCore.Entity, layoutEntity: ConfigurationLayout.Entity }[]>();
    React.useEffect(() => {(async () => {
        if (!selectorCoreEntity)
            return;

        const louverSizes = await serviceClient.selectLouvers(selectorCoreEntity);
        const louverLayouts = await Bluebird.map(louverSizes, async (size) => ({
            coreEntity: size,
            layoutEntity: await serviceClient.createLayoutEntity(size)
        }));
        console.log('louverLayouts', louverLayouts)

        setLouverSizes(louverLayouts);
        // setLouverSelection(louverSelection);
    })().catch(err => {
        enqueueSnackbar('[Louver Selection] ' + err.toString(), { variant: 'error' });
        console.error(err)
    })}, [ serviceClient, selectorCoreEntity ]);

    const [ productCoreEntity, setProductCoreEntity ] = React.useState<ConfigurationCore.Entity>();
    React.useEffect(() => {(async () => {
        const entityModel = await serviceClient.getEntity('LV/LouverConfigurationSelector_Schema');
        const coreEntity = await serviceClient.createCoreEntity(entityModel);
        console.log('PRODUCT', coreEntity)

        setProductCoreEntity(coreEntity);
    })().catch(err => {
        enqueueSnackbar('[Product Entity Creation] ' + err.toString(), { variant: 'error' });
        console.error(err)
    })}, [ serviceClient ]);

    const [ performanceSizesLayoutGrid, setPerformanceSizesLayoutGrid ] = React.useState<ConfigurationLayoutGrid.Grid>();
    React.useEffect(() => {(async () => {
        if (!louverSizes)
            return;

        const gridModel = await serviceClient.getGrid('louverPerformanceSizes');
        const grid = await serviceClient.createLayoutGrid(louverSizes.map(s => s.coreEntity), gridModel);
        setPerformanceSizesLayoutGrid(grid);
    })().catch(err => {
        enqueueSnackbar('[Performance Sizes Layouts Grid Creation] ' + err.toString(), { variant: 'error' });
        console.error(err)
    })}, [ serviceClient, louverSizes ]);

    const onSelectorChange: LayoutEntityProps['onChange'] = (e) => {
        if (!selectorCoreEntity)
            return;

        (async () => {
            const updateResults = await serviceClient.updateEntity(selectorCoreEntity, e.state);
            setSelectorCoreEntity(new ConfigurationCore.Entity(selectorCoreEntity));
        })().catch(err => {
            enqueueSnackbar('[Updating Selector Entity] ' + err.toString(), { variant: 'error' });
            console.error(err)
        });
    }
    const onProductChange: LayoutEntityProps['onChange'] = (e) => {
        if (!productCoreEntity)
            return;

        (async () => {
            console.log('UPDATE', e);
            const updateResults = await serviceClient.updateEntity(productCoreEntity, e.state);
            console.log('PRODUCT', productCoreEntity)
            setProductCoreEntity(new ConfigurationCore.Entity(productCoreEntity));
        })().catch(err => {
            enqueueSnackbar('[Updating Product Entity] ' + err.toString(), { variant: 'error' });
            console.error(err)
        });
    }
    const onProductSizeChange: LayoutEntityProps['onChange'] = (e) => {
        if (!productCoreEntity)
            return;

        (async () => {
            console.log('UPDATE', e);
            const updateResults = await serviceClient.updateEntity(productCoreEntity, e.state);
            console.log('PRODUCT', productCoreEntity)
            setProductCoreEntity(new ConfigurationCore.Entity(productCoreEntity));
        })().catch(err => {
            enqueueSnackbar('[Updating Product Entity] ' + err.toString(), { variant: 'error' });
            console.error(err)
        });
    }

    const onTabChange = (idx: number) => {
        setPreferences(prev => ({
            ...prev,
            louverSelectionTab: tabs[idx].name
        }));

        history.push(`/louver-selection/${tabs[idx].name}`);
    }

    const onAddClick = () => {
        if (!productCoreEntity || !louverSizes)
            return;

        (async () => {
            for (const sizeId of selectedPerformanceSizeIds) {
                const louverSize = louverSizes.find(s => s.coreEntity.entityId === sizeId);
                if (!louverSize)
                    continue;

                const louverSizeEntity = louverSize.coreEntity;
                timeLog('createIntegrationProjection')
                const louverSizeIntegrationEntity = await serviceClient.createIntegrationProjection(louverSizeEntity);
                timeLog('INTEGRATION ENTITY', louverSizeIntegrationEntity);
    
                timeLog('addChild')
                await serviceClient.addChild(productCoreEntity, 'LouverConfigurationSelector_To_LouverProduct', louverSizeIntegrationEntity);
                // timeLog('applyIntegration')
                // await serviceClient.applyIntegration(productCoreEntity, 'LouverConfigurationSelector_To_LouverProduct', louverSizeIntegrationEntity);
                timeLog('PRODUCT', productCoreEntity)
    
                enqueueSnackbar('Louver Size Added', { variant: 'success' });
    
                // const entityModel = await serviceClient.getEntity('LV/Louver_Product_Schema');
                // const coreEntity = await serviceClient.createCoreEntity(entityModel);
    
                // coreEntity.constraintModels.push(ConfigurationCore.LinkedConstraintModel.parse({
                //     name: 'ParentMapper',
                //     constraintModel: {
                //         engine: {
                //             engineType: 'mapper'
                //         },
                //         mapper: {
                //             mappings: [
                //                 { source: 'Model', destination: 'Model', direction: 'From' }
                //             ]
                //         }
                //     }
                // }));
                // var child = new ConfigurationCore.Child({
                //     name: 'child',
                //     type: 'size',
                //     entity: coreEntity
                // });
                // productCoreEntity.children.push(child);
    
                // // const newChild = productCoreEntity.children[0].clone();
                // // newChild.entity.entityId = UUID();
                // // productCoreEntity.children.push(newChild);
    
                // const updateResults = await serviceClient.updateEntity(productCoreEntity);
            }

            setProductCoreEntity(new ConfigurationCore.Entity(productCoreEntity));
        })().catch(err => {
            enqueueSnackbar('[Appending Product Entity] ' + err.toString(), { variant: 'error' });
            console.error(err)
        });
    }

    const [ selectedPerformanceSizeIds, setSelectedPerformanceSizeIds ] = React.useState<string[]>([]);
    const onPerformanceGridSelectionChanged: LayoutGridProps['onSelectionChanged'] = (ids) => {
        setSelectedPerformanceSizeIds(ids);
    }

    return (
        <React.Fragment>
            <Grid container xs={12} spacing={1} alignItems="flex-start">
                <Grid item container xs={4} spacing={1} direction="column" justify="flex-start">
                    <Grid item xs>
                        <AppBar position="static">
                            <Toolbar variant="dense">
                                <Typography variant="h6">Performance Selector</Typography>
                            </Toolbar>
                        </AppBar>
                        {selectorLayoutEntity && <LayoutEntity entity={selectorLayoutEntity} onChange={onSelectorChange} />}
                    </Grid>

                    {performanceSizesLayoutGrid && <Grid item xs>
                        <Paper>
                            <AppBar position="static">
                                <Toolbar variant="dense">
                                    <Typography variant="h6">Performance Sizes</Typography>
                                    <div className={classes.grow}></div>
                                    <IconButton edge="end" color="inherit" onClick={onAddClick}>
                                        <Icons.Add />
                                    </IconButton>
                                </Toolbar>
                            </AppBar>
                            <LayoutGrid
                                grid={performanceSizesLayoutGrid}
                                onSelectionChanged={onPerformanceGridSelectionChanged}
                            />
                        </Paper>
                    </Grid>}
                </Grid>

                <Grid item container xs={8} spacing={1} direction="column" justify="flex-start">
                    {productCoreEntity && <Grid item xs>
                        <AppBar position="static" square={false}>
                            <Tabs value={tabs.findIndex(t => t.name === tab)} onChange={(e, value) => onTabChange(value)}>
                                {tabs.map((tab, tabIndex) => <Tab label={tab.label} tabIndex={tabIndex} />)}
                            </Tabs>
                        </AppBar>

                        <Switch>
                            {tabs.map((tab) => (
                                <Route path={`/louver-selection/${tab.name}`}>
                                    <tab.element entity={productCoreEntity} onChange={onProductChange} onSizeChange={onProductSizeChange} />
                                </Route>
                            ))}
                        </Switch>
                    </Grid>}
                </Grid>
            </Grid>
        </React.Fragment>
    );
}

interface ProductEntityTabProps {
    entity: ConfigurationCore.Entity;
    onChange?: LayoutEntityProps['onChange'];
    onSizeChange?: LayoutEntityProps['onChange'];
}
function SizingTab({ entity, onChange, onSizeChange }: ProductEntityTabProps) {
    const { enqueueSnackbar } = useSnackbar();

    const serviceClient = useServiceClient();

    const [ productLayoutEntity, setProductLayoutEntity ] = React.useState<ConfigurationLayout.Entity>();
    React.useEffect(() => {(async () => {
        if (!entity)
            return;

        const layoutModel = await serviceClient.getLayout('LV/LouverConfigurationSelector_Layout');
        // const layoutEntity = await serviceClient.createLayoutEntity(entity, layoutModel);
        const layoutEntity = await serviceClient.createLayoutEntity(entity);
        enqueueSnackbar('Product Updated', { variant: 'info' });

        setProductLayoutEntity(layoutEntity);
    })().catch(err => {
        enqueueSnackbar('[Product Layout Creation] ' + err.toString(), { variant: 'error' });
        console.error(err)
    })}, [ serviceClient, entity ]);

    // const [ louverSizeLayoutEntities, setLouverSizeLayoutEntities ] = React.useState<ConfigurationLayout.Entity[]>([]);
    // React.useEffect(() => {(async () => {
    //     if (!entity)
    //         return;

    //     const layoutEntities = await Bluebird.map(entity.children.filter(c => c.type = 'size'), child => serviceClient.createLayoutEntity(child.entity));
    //     console.log(layoutEntities)

    //     setLouverSizeLayoutEntities(layoutEntities);
    // })().catch(err => {
    //     enqueueSnackbar('[Size Layouts Creation] ' + err.toString(), { variant: 'error' });
    //     console.error(err)
    // })}, [ serviceClient, entity ]);

    const [ louverSizeLayoutGrid, setLouverSizeLayoutGrid ] = React.useState<ConfigurationLayoutGrid.Grid>();
    React.useEffect(() => {(async () => {
        if (!entity)
            return;

        const gridModel = await serviceClient.getGrid('louverSizes');
        const grid = await serviceClient.createLayoutGrid(entity, gridModel);
        setLouverSizeLayoutGrid(grid);
    })().catch(err => {
        enqueueSnackbar('[Size Layouts Creation] ' + err.toString(), { variant: 'error' });
        console.error(err)
    })}, [ serviceClient, entity ]);

    const onGridChange: LayoutGridProps['onChange'] = (props) => {
        const matchedChild = entity.children.find(c => c.entity.entityId === props.id);
        if (!matchedChild)
            throw new Error('Child for entity not found');

        const entityState: Record<string, string> = {};
        for (let key in props.state)
            entityState[`${matchedChild.name}.${key}`] = props.state[key];

        onChange?.({
            // entity,
            state: entityState
        });
    }

    return (
        <React.Fragment>
            <Grid container xs={12} spacing={1} alignItems="flex-start">
                <Grid item xs={12}>
                    {productLayoutEntity && <LayoutEntity entity={productLayoutEntity} onChange={onChange} />}
                </Grid>
                <Grid item xs={12}>
                    <Paper>
                        <AppBar position="static">
                            <Toolbar variant="dense">
                                <Typography variant="h6">Sizes</Typography>
                            </Toolbar>
                        </AppBar>
                        {louverSizeLayoutGrid && <LayoutGrid
                            grid={louverSizeLayoutGrid}
                            onChange={onGridChange}
                        />}
                    </Paper>
                </Grid>
            </Grid>
        </React.Fragment>
    );
}
function AccessoriesTab({ entity, onChange }: ProductEntityTabProps) {
    const { enqueueSnackbar } = useSnackbar();

    const serviceClient = useServiceClient();

    const [ productLayoutEntity, setProductLayoutEntity ] = React.useState<ConfigurationLayout.Entity>();
    React.useEffect(() => {(async () => {
        if (!entity)
            return;

        // const layoutModel = await serviceClient.getLayout('LV/LouverConfigurationSelector_Layout');
        const layoutEntity = await serviceClient.createLayoutEntity(entity);

        setProductLayoutEntity(layoutEntity);
    })().catch(err => {
        enqueueSnackbar('[Product Layout Creation] ' + err.toString(), { variant: 'error' });
        console.error(err)
    })}, [ serviceClient, entity ]);

    return (
        <React.Fragment>
            {productLayoutEntity && <LayoutEntity entity={productLayoutEntity} onChange={onChange} />}
        </React.Fragment>
    );
}

function timeLog(message?: any, ...optionalParams: any[]) {
    const date = new Date();

    console.log(`${date.getHours()}:${date.getMinutes()}:${date.getSeconds()}.${date.getMilliseconds()}`, message, ...optionalParams);
}